// components/EventContent/OrganizerBlock.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Button from '../Button/Button';
import Typography from '../Typography';
import { theme } from '../../theme';
import { getPlatform, getIcon } from '../../utils/socialMediaUtils'
import { useTranslation } from 'react-i18next';
import HelmetIcon from '../Icons/HelmetIcon';
import ContactOrganizerModal from '../Modal/ContactOrganizerModal';
import { useAuth } from '../../context/AuthContext';
import axiosClient from '../../api/axiosClient';
import { useNavigate, useLocation } from 'react-router-dom';

const OrganizerBlock = ({ organizer, eventId }) => {
  const { t: tEventDetail } = useTranslation('eventDetail');
  const { isAuthenticated, isFollowing: ctxIsFollowing, followingReady, follow, unfollow, user } = useAuth();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState(null);
  const [followingCount, setFollowingCount] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // No early return before hooks; handle below before render

  const handleContactClick = () => {
    if (!isAuthenticated) {
      const returnTo = `${location.pathname}?openContactModal=true`;
      navigate('/signin', { state: { returnTo } });
      return;
    }
    setIsContactModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsContactModalOpen(false);
  };

  useEffect(() => {
    const loadFollowersCount = async () => {
      try {
        const oid = organizer?._id || organizer?.id;
        if (!oid) return;
        const { data } = await axiosClient.get(`/api/users/${oid}/followers/count`);
        setFollowersCount(Number(data?.count || 0));
      } catch (e) {
        setFollowersCount(0);
      }
    };
    const loadFollowingCount = async () => {
      try {
        const oid = organizer?._id || organizer?.id;
        if (!oid) return;
        const { data } = await axiosClient.get(`/api/users/${oid}/following/count`);
        setFollowingCount(Number(data?.count || 0));
      } catch (e) {
        setFollowingCount(0);
      }
    };
    loadFollowersCount();
    loadFollowingCount();
  }, [organizer?._id, organizer?.id]);

  const isFollowing = organizer?._id ? ctxIsFollowing(organizer._id) : false;
  const isOwnOrganizer = Boolean(user?.id && organizer?._id && String(user.id) === String(organizer._id));

  const handleFollowClick = async () => {
    if (!isAuthenticated) {
      const returnTo = `${location.pathname}`;
      navigate('/signin', { state: { returnTo } });
      return;
    }
    if (!organizer?._id) return;
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await unfollow(organizer._id);
        setFollowersCount(prev => prev != null ? Math.max(0, prev - 1) : prev);
      } else {
        await follow(organizer._id);
        setFollowersCount(prev => prev != null ? prev + 1 : prev);
      }
    } catch (e) { }
    finally {
      setFollowLoading(false);
    }
  };

  if (!organizer) {
    return null;
  }

  return (
    <OrganizerContainer>
      <Typography $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-1-semibold" as='p'>
        {tEventDetail('eventOrganizer.title')}
        {/* Sobre el organizador */}
      </Typography>
      <OrganizerCard>
        <OrganizerHeader>
          <OrganizerInfo>
            {/* <OrganizerAvatar src={organizer.userAvatar} alt="Organizador" /> */}
            {organizer.userAvatar ? (
              <OrganizerAvatar src={organizer.userAvatar} alt="Organizador" />
            ) : (
              <AvatarPlaceholder>
                <HelmetIcon width={40} height={40} fill={theme.colors.defaultStrong} />
              </AvatarPlaceholder>
            )}

            <OrganizerData>
              <Typography $variant='body-1-semibold' as='p'>
                {organizer.name} {organizer.lastName}
              </Typography>
              <CountersRow>
                <CounterLink onClick={() => {
                  const currentSlug = (typeof window !== 'undefined' ? window.location.pathname.split('/').slice(-1)[0] : '')
                  navigate(`/user/${organizer._id}/connections?tab=followers&from=event&eventId=${eventId}&slug=${currentSlug}`, { state: { from: 'event', eventId: eventId, slug: currentSlug } })
                }}>
                  <Typography $variant='body-2-semibold' as='p' color={theme.colors.defaultWeak}>
                    {typeof followersCount === 'number' ? followersCount : ''} {tEventDetail('eventOrganizer.followers')}
                  </Typography>
                </CounterLink>
                <CounterLink onClick={() => {
                  const currentSlug = (typeof window !== 'undefined' ? window.location.pathname.split('/').slice(-1)[0] : '')
                  navigate(`/user/${organizer._id}/connections?tab=following&from=event&eventId=${eventId}&slug=${currentSlug}`, { state: { from: 'event', eventId: eventId, slug: currentSlug } })
                }}>
                  <Typography $variant='body-2-semibold' as='p' color={theme.colors.defaultWeak}>
                    {typeof followingCount === 'number' ? followingCount : ''} {tEventDetail('eventOrganizer.following')}
                  </Typography>
                </CounterLink>
              </CountersRow>
            </OrganizerData>
          </OrganizerInfo>
          <OrganizerActions>
            {!isOwnOrganizer && (
              <Button
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                $variant="ghost"
                onClick={handleContactClick}
              >
                {tEventDetail('eventOrganizer.contactButton')}
              </Button>
            )}
            {followingReady && !isOwnOrganizer && (
              isFollowing ? (
                <Button size="l" sizeTablet="l" sizeMobile="m" $variant="outline" onClick={handleFollowClick} disabled={followLoading}>
                  Dejar de seguir
                </Button>
              ) : (
                <Button size="l" sizeTablet="l" sizeMobile="m" $variant="brand" onClick={handleFollowClick} disabled={followLoading}>
                  {tEventDetail('eventOrganizer.followButton')}
                </Button>
              )
            )}
          </OrganizerActions>
        </OrganizerHeader>
        {/* <Typography $variant='body-1-regular' as='p'>{organizer.description}</Typography> */}
        {organizer.description && organizer.description.trim() !== '' && (
          <Typography $variant='body-1-regular' as='p'>{organizer.description}</Typography>
        )}
        {organizer.socialMediaLinks && organizer.socialMediaLinks.length > 0 && (
          <SocialMediaList>
            {organizer.socialMediaLinks.map((link) => {
              // const iconPath = getIcon(link.platform);
              const platform = getPlatform(link.url);
              const IconComponent = getIcon(platform);

              return (
                <SocialMediaItem
                  key={link._id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.platform}
                >
                  <IconLink href={link.url} target='_blank' rel='noreferrer'>
                    {/* <img src={icon} alt={platform} /> */}
                    <IconComponent fill={theme.colors.defaultWeak} />
                  </IconLink>
                  {/* <SocialMediaIcon src={iconPath} alt={`${link.platform} icon`} /> */}
                </SocialMediaItem>
              );
            })}
          </SocialMediaList>
        )}
      </OrganizerCard>

      {/* Modal de contacto */}
      {isContactModalOpen && (
        <ContactOrganizerModal
          isOpen={isContactModalOpen}
          onClose={handleCloseModal}
          eventId={eventId}
          organizerName={`${organizer.name} ${organizer.lastName}`}
        />
      )}
    </OrganizerContainer>
  );
};

export default OrganizerBlock;

// Styled Components
const OrganizerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const OrganizerCard = styled.div`
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.sizing.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 12px;
  }
`;

const OrganizerHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.sm};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.sizing.sm};
  }
`;

const OrganizerInfo = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
`;

const OrganizerAvatar = styled.img`
  border-radius: ${({ theme }) => theme.radius.xs};
  height: 56px;
  width: 56px;
`;

const OrganizerData = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};
`;

const CountersRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const CounterLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 300ms ease-in;

  &:hover {
    opacity: 0.6;
  }
`;

const OrganizerActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row-reverse;
  }
`;


const IconLink = styled.a`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  opacity: 0.8;
  padding: ${({ theme }) => theme.sizing.xs};
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultSubtle};
    border: 1px solid ${({ theme }) => theme.border.defaultWeak};
    opacity: 1;
  }
`;

const SocialMediaList = styled.ul`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: flex-start;
  list-style: none;
  margin-top:  ${({ theme }) => theme.sizing.xs};
  padding: 0;
`;

const SocialMediaItem = styled.li`
`;

const AvatarPlaceholder = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  display: flex;
  height: 56px;
  justify-content: center;
  width: 56px;
`;
